import React from 'react';
import { Icon } from '@iconify/react';
import { Tooltip } from '@nextui-org/react';
import { AppLink, type AppLinkTarget } from './AppLink';

export interface NavSidebarItem {
  key: string;
  path: string;
  label: string;
  icon: string;
  target?: AppLinkTarget;
}

export interface NavSidebarGroup {
  heading: string | null;
  items: NavSidebarItem[];
}

export interface NavSidebarProps {
  /** Navigation groups (heading + items) */
  navGroups: NavSidebarGroup[];
  /** Current active section key (e.g. 'profile', 'behavior') */
  currentSection: string;
  /** Collapsed state: when true only icons are shown */
  isCollapsed: boolean;
  /** Toggle collapse */
  onToggleCollapse: () => void;
  /** Header title shown when not collapsed (e.g. 'Settings') */
  headerTitle: string;
  /** Returns true if the nav item with this key is disabled */
  isDisabled?: (key: string) => boolean;
  /** Optional class for the aside root */
  className?: string;
  /** Tooltip when collapsed (e.g. "Expand menu") */
  expandMenuLabel?: string;
  /** Tooltip when expanded (e.g. "Collapse menu") */
  collapseMenuLabel?: string;
  /** When set, internal nav items emit this instead of navigating; parent can run custom logic then navigate */
  onInternalNavigate?: (path: string, item: NavSidebarItem) => void;
  navClassName?: string;
  /** Header title and collapse control. Settings hover menu hides both. */
  showHeader?: boolean;
  /** Option label classes. Defaults to 16px. */
  labelClassName?: string;
  /** Group heading classes. Defaults to 12px grey. */
  headingClassName?: string;
  /** `app` matches the main sidebar: 8px radius, 2px item gap, and the same hover and active fill. */
  variant?: 'default' | 'app';
}

/**
 * Sidebar (aside) with collapsible nav. Uses AppLink: external = new tab, internal/undefined = in-app navigation.
 */
export function NavSidebar(props: NavSidebarProps) {
  const {
    navGroups,
    currentSection,
    isCollapsed,
    onToggleCollapse,
    headerTitle,
    isDisabled = () => false,
    className = '',
    navClassName = '',
    expandMenuLabel = 'Expand menu',
    collapseMenuLabel = 'Collapse menu',
    onInternalNavigate,
    showHeader = true,
    labelClassName = 'text-[16px] font-medium leading-5',
    headingClassName = 'px-3 pt-[14px] pb-1 text-xs font-medium text-[#a1a1aa]',
    variant = 'default',
  } = props;
  const matchAppNav = variant === 'app';

  const asideClass = `nav-sidebar flex-shrink-0 flex flex-col border-r border-default-200 bg-content1 transition-[width] duration-200 ease-in-out ${matchAppNav ? 'w-fit' : isCollapsed ? 'w-[72px]' : 'w-[220px]'} ${className}`.trim();

  return (
    <aside className={asideClass}>
      {showHeader && (
        <div className="flex items-center gap-3 border-b border-default-200 min-h-[48px] px-3">
          {!isCollapsed && (
            <span className="flex-1 truncate px-0 text-[16px] font-medium leading-5 text-[#181818] dark:text-foreground">
              {headerTitle}
            </span>
          )}
          <Tooltip content={isCollapsed ? expandMenuLabel : collapseMenuLabel} placement="right">
            <button
              type="button"
              onClick={onToggleCollapse}
              className="mx-auto flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md text-[#71717a] transition-colors hover:bg-default-100 dark:text-default-500"
              aria-label={isCollapsed ? expandMenuLabel : collapseMenuLabel}
            >
              <Icon icon={isCollapsed ? 'mdi:chevron-right' : 'mdi:chevron-left'} width={20} height={20} />
            </button>
          </Tooltip>
        </div>
      )}
      <nav className={`flex-1 overflow-y-auto ${matchAppNav ? 'w-fit px-3 py-3.5' : 'py-2'} ${navClassName ? navClassName : ''}`} style={{ scrollbarWidth: 'thin', scrollbarColor: '#E5E7EB transparent' }}>
        {navGroups.map((group) => (
          <div key={group.heading} className={matchAppNav ? undefined : 'mb-1'}>
            {!isCollapsed && group.heading && (
              <div className={headingClassName}>
                {group.heading}
              </div>
            )}
            {group.items.map((item) => {
              const active = currentSection.toLowerCase() === item.key.toLowerCase();
              const disabled = isDisabled(item.key);
              const target = item.target ?? 'internal';
              const linkClass = (matchAppNav
                ? `
                mb-0.5 flex w-full items-center whitespace-nowrap border-0 text-left
                ${isCollapsed ? 'justify-center rounded-[8px] px-0 py-2 min-h-[36px]' : 'gap-3 rounded-[8px] px-3 py-[10px]'}
                ${active
                  ? 'cty-nav-item-active font-[600] text-primary dark:text-[var(--cty-primary-accent)]'
                  : 'bg-transparent font-medium text-[#181818] hover:bg-[#F4F4F5] dark:text-foreground dark:hover:bg-content2'
                }
              `
                : `
                w-full flex items-center text-left transition-colors border-l-2
                ${isCollapsed ? 'justify-center px-0 py-2 min-h-[36px]' : 'gap-3 px-3 py-[10px]'}
                ${active
                  ? 'bg-default-100 text-[#181818] dark:text-foreground border-l-primary'
                  : 'text-[#181818] dark:text-foreground hover:bg-default-100 border-l-transparent'
                }
              `
              ).replace(/\s+/g, ' ').trim() + (disabled ? ' opacity-50 cursor-not-allowed' : ' cursor-pointer');
              const content = (
                <>
                  {isCollapsed ? (
                    <Tooltip content={item.label} placement="right">
                      <span className="flex-shrink-0">
                        <Icon icon={item.icon} width={18} height={18} className="text-[#71717a] dark:text-default-500" />
                      </span>
                    </Tooltip>
                  ) : (
                    <Icon
                      icon={item.icon}
                      width={18}
                      height={18}
                      className={`flex-shrink-0 ${active && matchAppNav ? 'text-primary dark:text-[var(--cty-primary-accent)]' : 'text-[#71717a] dark:text-default-500'}`}
                    />
                  )}
                  {!isCollapsed && <span className={labelClassName}>{item.label}</span>}
                </>
              );
              const linkEl = (
                <AppLink
                  to={item.path}
                  target={target}
                  className={linkClass}
                  style={active && !matchAppNav ? { borderLeftColor: 'var(--cty-primary, #2563EB)' } : undefined}
                  disabled={disabled}
                  aria-label={item.label}
                  aria-current={active ? 'page' : undefined}
                  onInternalClick={
                    target === 'internal' && onInternalNavigate
                      ? (path) => onInternalNavigate(path, item)
                      : undefined
                  }
                >
                  {content}
                </AppLink>
              );
              return isCollapsed ? (
                <Tooltip key={item.key} content={item.label} placement="right">
                  {linkEl}
                </Tooltip>
              ) : (
                <div key={item.key}>{linkEl}</div>
              );
            })}
          </div>
        ))}
      </nav>
    </aside>
  );
}

export default NavSidebar;
